import React from "react";
import {
  Box,
  Grid,
  GridItem,
  HStack,
  Heading,
  SimpleGrid,
  Stack,
  Text,
  useToken,
} from "@chakra-ui/react";
import { FiArrowRight } from "react-icons/fi";
import { Link as GatsbyLink, graphql, useStaticQuery } from "gatsby";
import { HomePageSection, HomePageSectionHeading } from "./HomePage";

type CertificationCardProps = {
  certification: Pick<
    Queries.Certification,
    "slug" | "tagline" | "gradient" | "level" | "cta"
  >;
  colSpan: {
    [propName: string]: number;
  };
};

function CertificationCard({
  certification,
  colSpan = { base: 1 },
}: CertificationCardProps) {
  const { slug, cta, tagline, gradient, level } = certification;

  const colors = useToken("colors", Array.from(gradient));

  return (
    <GridItem
      as={GatsbyLink}
      to={`/certifications/${slug}`}
      color="white"
      rounded="2xl"
      colSpan={colSpan}
      p="10"
      pos="relative"
      overflow="hidden"
      bg={gradient[1]}
      _before={{
        content: '""',
        bgImage: `linear-gradient(${["to top right", ...colors]})`,
        pos: "absolute",
        top: 0,
        left: 0,
        boxSize: "full",
        opacity: 0,
        transitionProperty: "opacity",
        transitionDuration: "normal",
        width: "100%",
      }}
      _hover={{
        _before: {
          opacity: 1,
        },
      }}
      role="group"
      zIndex={0}
    >
      <Stack zIndex={1} spacing="12" align="flex-start" pos="relative">
        <Box
          rounded={4}
          borderColor="whiteAlpha.300"
          borderWidth={1}
          px="2"
          lineHeight="taller"
          textStyle="smallCaps"
        >
          Level {level}
        </Box>
        <Box>
          <Heading mb="1" as="h3" size="lg">
            {cta}
          </Heading>
          <Text fontSize="lg">{tagline}</Text>
        </Box>
        <HStack fontSize="lg" fontWeight="semibold">
          <span>View certification details</span>
          <Box
            as={FiArrowRight}
            transitionProperty="transform"
            transitionDuration="normal"
            _groupHover={{
              transform: "translateX(25%)",
            }}
          />
        </HStack>
      </Stack>
    </GridItem>
  );
}

export function HomePageCertifications() {
  const data = useStaticQuery<Queries.HomePageCertificationsQuery>(
    graphql`
      query HomePageCertifications {
        certifications: allCertification(
          filter: { unlisted: { ne: true } }
          sort: { fields: level }
        ) {
          nodes {
            id
            slug
            cta
            tagline
            gradient
            level
          }
        }
      }
    `
  );

  const [connectorsCertNode, certifications] = [
    data.certifications.nodes.find(
      ({ slug }) => slug === "apollo-orchestration-associate"
    ),
    data.certifications.nodes.filter(
      (cert) => cert.slug !== "apollo-orchestration-associate"
    ),
  ];

  return (
    <HomePageSection id="certifications">
      <HomePageSectionHeading
        title="Become a certified Apollo Graph Developer"
        description="Let's find the path that works for you right now. Where are you in
          your certification journey?"
        mb={{
          base: 6,
          md: 8,
          lg: 12,
          xl: 14,
        }}
        textAlign={{ md: "center" }}
      />
      <SimpleGrid columns={{ md: 2 }} spacing="6">
        {connectorsCertNode && (
          <CertificationCard
            colSpan={{ md: 2 }}
            key={connectorsCertNode.id}
            certification={connectorsCertNode}
          />
        )}

        {certifications.map((certification) => (
          <CertificationCard
            key={certification.id}
            certification={certification}
          />
        ))}
      </SimpleGrid>
    </HomePageSection>
  );
}
